<!-- What a clip is doing, in front of everything else.
  --
  -- Same shape and the same argument as PdfProgress: this is work the visitor
  -- cannot see, cannot speed up and cannot work around, and on a laptop a card
  -- in the page sits below the fold -- so the one moment feedback is genuinely
  -- needed is the one moment it would be invisible.
  --
  -- Two things make it different from the PDF panel. There is no upload: the
  -- clip is read off the device and never leaves it, and only the GIF that
  -- comes out is ever submitted. And the count is REAL from the first frame --
  -- decodeFrames() knows how many frames it planned before it draws any, so
  -- there is no estimate to fall back to and none of the PDF panel's
  -- machinery for a poll that never answers.
  --
  -- The filename is here for the reason it is on the PDF panel: a clip has no
  -- thumbnail while it is being read, so the name is the only way to see it is
  -- the wrong file. break-all, because these are real filenames off a phone.
  -->
<template>
    <div v-if="uiStore.videoBusy || saving" class="fixed inset-0 z-50 flex items-center justify-center p-4"
         style="background: rgba(11, 9, 20, 0.72)"
         role="dialog" aria-modal="true" :aria-label="title">

        <div class="w-full max-w-md rounded-2xl border border-rule bg-raised p-7 flex flex-col items-center gap-5 text-center shadow-2xl">

            <svg class="h-16 w-16 animate-spin text-brand" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="4" class="opacity-20"></circle>
                <path d="M44 24a20 20 0 0 0-20-20" stroke="currentColor" stroke-width="4" stroke-linecap="round"></path>
            </svg>

            <div class="flex flex-col gap-1.5">
                <h2 class="text-xl font-black tracking-tight">{{title}}</h2>
                <p v-if="uiStore.videoName" id="video-name"
                   class="text-[15px] font-bold break-all leading-snug">{{uiStore.videoName}}</p>
            </div>

            <!-- A real bar, because the frame count is known before the first
              -- frame is drawn. No bar while the file is still being opened:
              -- there is nothing to measure yet and a bar at 0% that does not
              -- move reads as stuck. -->
            <div v-if="uiStore.videoTotal && !saving" class="w-full flex flex-col gap-2">
                <div class="h-2 w-full overflow-hidden rounded-full bg-rule">
                    <div class="h-full rounded-full bg-brand transition-[width] duration-150"
                         :style="`width: ${pct}%`"></div>
                </div>
                <p id="video-count" class="text-[15px] text-ink-soft tabular-nums">
                    {{ t('video.converting', { done: uiStore.videoDone, total: uiStore.videoTotal }) }}
                </p>
            </div>
            <p v-else id="video-phase" class="text-[15px] text-ink-soft">{{title}}</p>

            <!-- Nothing to count while it uploads: the bytes are one object
              -- and the CDN's own work is not observable from here. A sentence
              -- and a spinner is all there honestly is. -->
            <p v-if="saving" class="text-[15px] text-ink-soft">{{ t('video.savingHint') }}</p>
        </div>
    </div>
</template>

<script setup>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useUiElements } from "./../store/uiElements"

/* `saving` covers what happens AFTER the frames are made: burning the words
 * in, uploading the GIF, and waiting for the optimised version to come back.
 *
 * The same panel rather than a new one, and for the reason PdfProgress records:
 * this is work the visitor cannot see, cannot speed up and cannot work around,
 * and on a laptop a card in the page sits below the fold. It earned taking
 * over the screen once; a 1.7 MB GIF going up earns it too.
 *
 * Before this, the primary button simply VANISHED while the work ran --
 * `v-show="... && !workInFlight"` -- which is the least informative thing a
 * page can do at the one moment somebody is waiting on it. */
const props = defineProps({saving: {type: Boolean, default: false}})

const uiStore = useUiElements()
const { t } = useI18n()

/* Two sentences for three phases, and that is not a slip.
 *
 * The GIF is built WHILE the frames are extracted -- createGifSink().add() is
 * called from inside decodeFrames()'s callback, which is what keeps one
 * frame's pixels in memory instead of a hundred. So "putting the animation
 * together" is literally what is happening during `converting`, not a
 * placeholder for it.
 *
 * `assembling` is the encoder's final pass. It keeps the same wording because
 * the visitor is not doing anything differently, and it is a separate phase
 * only so that a panel reading "frame 100 of 100" does not sit there looking
 * stuck at the finish line while finish() runs. */
const title = computed(() => {
    if (props.saving && !uiStore.videoBusy) return t("video.saving")
    return uiStore.videoPhase === "reading" || !uiStore.videoPhase
        ? t("video.reading")
        : t("video.assembling")
})

const pct = computed(() => {
    if (!uiStore.videoTotal) return 0
    return Math.min(100, Math.round((uiStore.videoDone / uiStore.videoTotal) * 100))
})
</script>
